import { ArrowRightCircleIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import React from "react";

/**
 * Shared look for the Workflow Setup vendor tables (Access, Deletion, Opt-Out, Limit). It matches the Vendor List
 * tables on the Data Map (VendorInventoryPage and its BadgedTextPill), so the two areas read as one product.
 */
export const workflowTableClasses = {
  container:
    "border border-solid border-neutral-200 rounded-xl shadow-[0px_1px_2px_0px_#1018280D] max-h-[636px] overflow-y-auto relative",
  headerCell:
    "bg-neutral-0 border-b border-solid border-neutral-200 text-neutral-600 font-medium text-[12px] uppercase tracking-[0.4px]",
  row: "border-t border-solid border-gray-200 hover:bg-neutral-0",
  cell: "border-b border-solid border-gray-200",
  vendorName: "text-[#111827] font-medium text-[14px]",
};

export const workflowTableHeaderCellStyle: React.CSSProperties = {
  paddingTop: 11,
  paddingBottom: 11,
};

/** 16px vertical padding and an 18px line height give 60px rows around a 24px logo, as on the Vendor List. */
export const workflowTableCellStyle: React.CSSProperties = {
  paddingTop: 16,
  paddingBottom: 16,
  lineHeight: "18px",
};

/** Vendor name cells are inset 24px, like the Vendor List's first column. */
export const workflowTableVendorCellStyle: React.CSSProperties = {
  ...workflowTableCellStyle,
  paddingLeft: 24,
  paddingRight: 24,
};

/** The 12px status pill used in the table cells. Long values (e.g. an email address) are truncated. */
export function WorkflowTablePill({
  className,
  children,
}: {
  /** Text and background colour classes. */
  className: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={clsx(
        "inline-block max-w-[188px] truncate align-middle rounded-full px-[10px] py-[2px] font-medium text-[12px]",
        className,
      )}
      style={{ lineHeight: "14px" }}
    >
      {children}
    </div>
  );
}

/** The last column's arrow that opens the row's side panel, centred in its cell. */
export function WorkflowTableOpenCell({
  onOpen,
  vendorName,
  hidden = false,
}: {
  onOpen: () => void;
  /** Names the button for screen readers, e.g. "Open FedEx settings". */
  vendorName: string;
  hidden?: boolean;
}) {
  return (
    <td className={workflowTableClasses.cell} style={workflowTableCellStyle}>
      {!hidden && (
        <div className="flex justify-center">
          <button
            type="button"
            aria-label={`Open ${vendorName} settings`}
            className="flex items-center bg-transparent p-0 cursor-pointer"
            style={{ border: 0 }}
            onClick={(e) => {
              // The row opens the panel too; handle it once.
              e.stopPropagation();
              onOpen();
            }}
          >
            <ArrowRightCircleIcon
              className="w-[20px] h-[20px] text-primary-600"
              aria-hidden="true"
            />
          </button>
        </div>
      )}
    </td>
  );
}
